import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from 'react-bootstrap';
import { CompetitorModal } from '../Modals';
import { AiChatModal } from './AiChatModal';
import { MarkdownContent } from './MarkdownContent';

interface TextSelection {
  text: string;
  top: number;
  left: number;
}

interface AiResponseTextProps {
  text: string;
  /** User prompt / search text shown in the chat details modal. */
  promptText?: string;
  modelLabel?: string;
  modelIconSvg?: string | null;
  projectId?: string;
  /** Optional heading shown on the same row as actions. */
  label?: ReactNode;
  /** When false, hide copy / details controls. Default true. */
  showToolbar?: boolean;
  /** Scrollable preview max height. Default 20rem. */
  maxHeight?: string;
  onCreateCompetitor?: (data: {
    brandName: string;
    websiteUrl: string;
    aliases: string[];
  }) => Promise<void>;
}

export function AiResponseText({
  text,
  promptText,
  modelLabel,
  modelIconSvg,
  projectId,
  label,
  showToolbar = true,
  maxHeight = '20rem',
  onCreateCompetitor,
}: AiResponseTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<TextSelection | null>(null);
  const [competitorModalOpen, setCompetitorModalOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftBrandName, setDraftBrandName] = useState('');
  const [copied, setCopied] = useState(false);

  const clearSelection = useCallback(() => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const updateSelection = useCallback(() => {
    if (!projectId || !onCreateCompetitor) {
      setSelection(null);
      return;
    }

    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !containerRef.current) {
      setSelection(null);
      return;
    }

    const range = sel.getRangeAt(0);
    if (!containerRef.current.contains(range.commonAncestorContainer)) {
      setSelection(null);
      return;
    }

    const selectedText = sel.toString().trim();
    if (selectedText.length < 2) {
      setSelection(null);
      return;
    }

    const rect = range.getBoundingClientRect();
    setSelection({
      text: selectedText,
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, [projectId, onCreateCompetitor]);

  useEffect(() => {
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      setSelection(null);
    }

    function onScroll() {
      setSelection(null);
    }

    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, []);

  useEffect(() => {
    setCopied(false);
    clearSelection();
  }, [text, clearSelection]);

  function openCompetitorModal() {
    if (!selection) return;
    setDraftBrandName(selection.text);
    clearSelection();
    setCompetitorModalOpen(true);
  }

  async function handleSubmit(data: { brandName: string; websiteUrl: string; aliases: string[] }) {
    if (!onCreateCompetitor) return;
    setSaving(true);
    try {
      await onCreateCompetitor(data);
      setCompetitorModalOpen(false);
    } finally {
      setSaving(false);
    }
  }

  async function copyResponse() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  const canAddCompetitor = Boolean(projectId && onCreateCompetitor);
  const canOpenChat = Boolean(promptText?.trim());

  return (
    <div>
      {(label || showToolbar) && (
        <div className="mb-2 d-flex align-items-center justify-content-between gap-2">
          {label ? <h4 className="fs--1 fw-semibold text-uppercase text-500 mb-0">{label}</h4> : <span />}
          {showToolbar && (
            <div className="d-flex flex-shrink-0 align-items-center gap-1">
              {canOpenChat && (
                <Button
                  type="button"
                  size="sm"
                  variant="falcon-default"
                  className="py-0 px-2 fs--2"
                  onClick={() => setChatOpen(true)}
                >
                  جزئیات گفتگو
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="falcon-primary"
                className="py-0 px-2 fs--2"
                onClick={() => void copyResponse()}
              >
                {copied ? 'کپی شد!' : 'کپی'}
              </Button>
            </div>
          )}
        </div>
      )}

      <div
        ref={containerRef}
        onMouseUp={updateSelection}
        onKeyUp={updateSelection}
        className="rounded-3 border bg-body-tertiary p-3 fs--1 text-800"
        style={{ maxHeight, overflow: 'auto', userSelect: 'text' }}
      >
        <MarkdownContent text={text} />
      </div>

      {selection && canAddCompetitor && (
        <div
          className="position-fixed"
          style={{ top: selection.top, left: selection.left, zIndex: 1060, transform: 'translate(-50%, -100%)' }}
        >
          <Button
            type="button"
            size="sm"
            variant="falcon-primary"
            onMouseDown={e => e.preventDefault()}
            onClick={openCompetitorModal}
            className="shadow-sm"
          >
            <span className="fw-semibold me-1">+</span>
            Add competitor
          </Button>
        </div>
      )}

      <CompetitorModal
        open={competitorModalOpen}
        onClose={() => setCompetitorModalOpen(false)}
        initial={{ brandName: draftBrandName, websiteUrl: '', aliases: [] }}
        onSubmit={handleSubmit}
        saving={saving}
        title="افزودن رقیب"
      />

      {canOpenChat && (
        <AiChatModal
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          promptText={promptText!.trim()}
          responseText={text}
          modelLabel={modelLabel}
          modelIconSvg={modelIconSvg}
        />
      )}
    </div>
  );
}
